import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import { Calendar, Info, Shield, Truck, Loader } from 'lucide-react';

const RentalPage: React.FC = () => {
  const { products: filteredProducts, loading, error } = useProducts({ productType: 'rental' });

  useEffect(() => {
    document.title = "Rental Equipment | Strum Music";
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading rental equipment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading rental equipment</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <Calendar className="h-16 w-16 mx-auto mb-4 text-blue-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Rental Equipment
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Rent professional-grade instruments for your performances, recordings, or events. 
              Flexible terms with option to purchase.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Rental Benefits */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Calendar className="h-8 w-8 text-blue-600" />,
                title: "Flexible Terms",
                description: "Monthly rentals with minimum 1-month commitment. Extend as needed."
              },
              {
                icon: <Shield className="h-8 w-8 text-blue-600" />,
                title: "Insurance Coverage",
                description: "Optional insurance available to protect against damage or theft."
              },
              {
                icon: <Truck className="h-8 w-8 text-blue-600" />,
                title: "Free Delivery",
                description: "Free delivery and pickup within Delhi NCR area."
              }
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rental Info */}
      <section className="py-8 bg-blue-50">
        <div className="container-custom">
          <div className="bg-white rounded-lg p-6 border-l-4 border-blue-600">
            <div className="flex items-start">
              <Info className="h-6 w-6 text-blue-600 mt-1 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Rental Terms & Conditions</h3>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>• Minimum rental period: 1 month</li>
                  <li>• Security deposit required (fully refundable)</li>
                  <li>• Option to purchase at discounted rate (80% of retail price)</li>
                  <li>• Professional setup and maintenance included</li>
                  <li>• Valid ID and address proof required</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <div className="container-custom py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Available for Rent</h2>
        
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    Rental
                  </span>
                </div>
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Rental Items Available</h3>
            <p className="text-gray-600">Check back soon for new rental equipment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalPage;